import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '../../../components/organisms/Table';
import { TableCellModifier } from '../../../components/organisms/Table/TableCell';
import { isEmptyArray } from '../../../lib/typeGuards';
import ILink from '../../../types/ILink';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { SearchBreadcrumb } from '../../../components/molecules/SearchBreadcrumb';
import { ContentLink } from '../ContentLink';
import { Strong } from '../../../components/atoms/Strong/Strong';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { ContentParagraph, ContentParagraphModifier } from '../ContentParagraph';
import ModalDialogAdditionalScrollContainer from '../../../components/organisms/ModalDialog/ModalDialogAdditionalScrollContainer';

export type PageItem = {
  page?: ILink;
  breadcrumb: ILink[];
  count: number;
};

type MostPopularPagesTableProps = {
  items?: PageItem[];
  countLabelPostFix: string;
  caption: string;
  emptyNotice?: string;
};

export function MostPopularPagesTable({ items, countLabelPostFix, caption, emptyNotice }: MostPopularPagesTableProps) {
  const { t } = useSonTranslation('MostPopularPages');

  if (!items || isEmptyArray(items)) {
    return emptyNotice ? (
      <ContentParagraph modifier={ContentParagraphModifier.PADDING}>{emptyNotice}</ContentParagraph>
    ) : null;
  }

  const pageLabel = t('table.page');
  const countLabel = t('table.count', { countLabelPostFix });

  return (
    <ModalDialogAdditionalScrollContainer>
      <div className="most-popular-pages-table">
        <Table caption={<Strong>{caption}</Strong>} captionHidden={false}>
          <TableHead>
            <TableRow>
              <TableCell modifier={TableCellModifier.LOWPADDING}>{pageLabel}</TableCell>
              <TableCell
                modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING, TableCellModifier.S_WIDTH]}
              >
                {countLabel}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.page?.url ?? item.count}>
                <TableCell modifier={TableCellModifier.LOWPADDING} dataHead={pageLabel}>
                  {!item.page ? (
                    <span className="most-popular-pages-table__hidden-page-container">
                      <SVG svgSpriteId={Icons.CLOSED_LOCK} svgClass="most-popular-pages-table__lock-icon" />
                      {t('table.hiddenPage')}
                    </span>
                  ) : (
                    <>
                      <ContentLink to={item.page.url}>{item.page.name}</ContentLink>
                      {item.breadcrumb.length > 0 && <SearchBreadcrumb breadcrumbs={item.breadcrumb} clickable />}
                    </>
                  )}
                </TableCell>
                <TableCell
                  modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING]}
                  dataHead={countLabel}
                >
                  {item.count}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </ModalDialogAdditionalScrollContainer>
  );
}
